/**
 * Athletics-hub shared client helpers (SCH-244 follow-up dedup).
 *
 * Pure presentation/derivation utilities shared by the web grid, the web teams
 * table, and the mobile Athletics screen so the gender label mapping, the
 * sport-group heading fallback, and the distinct-axis collector live in ONE
 * place. No I/O, no framework imports — both web and mobile import from
 * `@school-journey/shared-types/api/athletics-hub`.
 */

import { ATHLETICS_HUB_OTHER_SPORT_KEY } from './common'
import type { AthleticsHubGridItem, AthleticsHubSportGroup } from './common'

/** Display label for the reserved null-sport / unmatched-heading bucket. */
export const ATHLETICS_HUB_OTHER_SPORT_LABEL = 'Other'

/**
 * Human-readable athletic gender label: `M` → Boys, `F` → Girls, `C` → Co-Ed.
 * Any other value passes through unchanged (passthrough default), so an
 * unexpected token still renders rather than vanishing.
 */
export function genderLabel(gender: string): string {
  if (gender === 'M') return 'Boys'
  if (gender === 'F') return 'Girls'
  if (gender === 'C') return 'Co-Ed'
  return gender
}

/**
 * Heading for a sport group: the reserved "Other" bucket reads "Other"; otherwise
 * the group's canonical `sport_name`, falling back to the first team carrying a
 * `sport_name`, then "Other". The backend wires `sport_name: null` for the "Other"
 * bucket and sets it to the bucket's display label for real sports.
 */
export function sportGroupHeading(group: AthleticsHubSportGroup): string {
  if (group.sport_key === ATHLETICS_HUB_OTHER_SPORT_KEY) return ATHLETICS_HUB_OTHER_SPORT_LABEL
  return group.sport_name ?? group.teams.find(t => t.sport_name)?.sport_name ?? ATHLETICS_HUB_OTHER_SPORT_LABEL
}

/** Flatten the sport-grouped response into a flat team list (display order preserved). */
export function flattenTeams(groups: AthleticsHubSportGroup[]): AthleticsHubGridItem[] {
  return groups.flatMap(group => group.teams)
}

/**
 * Distinct, sorted, non-null/non-blank values of one axis across a flat team
 * list. The blank-guard trims first so a single-space token counts as blank
 * (consistent trim-guard across web grid + web table + mobile — previously the
 * web table used a looser `if (value)`). The `pick` selector lets each surface
 * choose its own axis field (e.g. web `level` = school division vs mobile
 * `level_of_play` = athletic level) while sharing this collect mechanism. Group-
 * based callers flatten via `flattenTeams` first.
 */
export function distinctAxis(
  teams: AthleticsHubGridItem[],
  pick: (team: AthleticsHubGridItem) => string | null | undefined
): string[] {
  const values = new Set<string>()
  for (const team of teams) {
    const value = pick(team)
    if (value !== null && value !== undefined && value.trim() !== '') values.add(value)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}
